import { Component } from '@theme/component';

const STORAGE_KEY = 'popup-wholesale-login-dismissed';

/**
 * A popup that auto-shows after a configurable delay, prompting wholesale
 * customers to log in to their wholesale account. Dismissible via the close
 * button, the dismiss link, the backdrop, or Escape.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The popup dialog element.
 *
 * @extends Component<Refs>
 */
class PopupWholesaleLogin extends Component {
  requiredRefs = ['dialog'];

  /** @type {number | undefined} */
  #autoShowTimer;

  /** @type {number} */
  #previousScrollY = 0;

  /** @type {boolean} */
  get #isTestingMode() {
    return this.hasAttribute('data-testing-mode');
  }

  connectedCallback() {
    super.connectedCallback();

    // Don't auto-show in the theme editor unless testing
    if (Shopify.designMode && !this.#isTestingMode) return;

    if (!this.#isTestingMode && this.#isDismissed()) return;

    const delay = this.#isTestingMode ? 0 : Number(this.dataset.delay) || 2;
    this.#autoShowTimer = window.setTimeout(() => this.showPopup(), delay * 1000);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.#autoShowTimer) {
      clearTimeout(this.#autoShowTimer);
    }
    this.#unlockScroll();
  }

  /**
   * Show the popup dialog.
   */
  showPopup() {
    const { dialog } = this.refs;
    if (dialog.open) return;

    this.#previousScrollY = window.scrollY;

    requestAnimationFrame(() => {
      document.body.style.width = '100%';
      document.body.style.position = 'fixed';
      document.body.style.top = `-${this.#previousScrollY}px`;

      dialog.showModal();

      dialog.addEventListener('click', this.#handleDialogClick);
      dialog.addEventListener('keydown', this.#handleDialogKeydown);
    });
  }

  /**
   * Close the popup dialog with animation.
   */
  async #closePopup() {
    const { dialog } = this.refs;
    if (!dialog.open) return;

    dialog.removeEventListener('click', this.#handleDialogClick);
    dialog.removeEventListener('keydown', this.#handleDialogKeydown);

    dialog.classList.add('popup-wholesale-login__dialog--closing');

    await new Promise((resolve) => {
      dialog.addEventListener('animationend', resolve, { once: true });
      // Fallback if animation doesn't fire
      setTimeout(resolve, 300);
    });

    this.#unlockScroll();
    window.scrollTo({ top: this.#previousScrollY, behavior: 'instant' });

    dialog.close();
    dialog.classList.remove('popup-wholesale-login__dialog--closing');
  }

  #unlockScroll() {
    document.body.style.width = '';
    document.body.style.position = '';
    document.body.style.top = '';
  }

  /**
   * Handle dismiss — close popup and save dismissal timestamp.
   */
  handleDismiss() {
    if (!this.#isTestingMode) {
      this.#saveDismissal();
    }
    this.#closePopup();
  }

  /**
   * Record a dismissal when the visitor follows a link out of the popup, so
   * it doesn't reappear on the login or apply page.
   */
  handleLoginClick() {
    if (!this.#isTestingMode) {
      this.#saveDismissal();
    }
  }

  /**
   * Close when clicking the backdrop (outside the content).
   *
   * @param {MouseEvent} event
   */
  #handleDialogClick = (event) => {
    if (event.target === this.refs.dialog) {
      this.handleDismiss();
    }
  };

  /**
   * Close on Escape key.
   *
   * @param {KeyboardEvent} event
   */
  #handleDialogKeydown = (event) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.handleDismiss();
    }
  };

  /**
   * Check if the popup was recently dismissed.
   *
   * @returns {boolean}
   */
  #isDismissed() {
    try {
      const dismissed = localStorage.getItem(STORAGE_KEY);
      if (!dismissed) return false;

      const dismissedAt = Number(dismissed);
      const dismissalHours = Number(this.dataset.dismissalHours) || 168; // default 7 days
      const hoursElapsed = (Date.now() - dismissedAt) / (1000 * 60 * 60);

      return hoursElapsed < dismissalHours;
    } catch {
      return false;
    }
  }

  /**
   * Save dismissal timestamp to localStorage.
   */
  #saveDismissal() {
    try {
      localStorage.setItem(STORAGE_KEY, String(Date.now()));
    } catch {
      // Storage unavailable — silently ignore
    }
  }
}

if (!customElements.get('popup-wholesale-login')) {
  customElements.define('popup-wholesale-login', PopupWholesaleLogin);
}
